// index.tsx
import "./index.css";
import "core-js/es/object/values";

import * as Sentry from "@sentry/react";
import log from "loglevel";

import { appStage, googleClientId, nodeEnv } from "@/environment";
import { configureStore, type StoreEnhancer } from "@reduxjs/toolkit";

import App from "@/App";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Integrations } from "@sentry/tracing";
import { Provider } from "react-redux";
import { SocketProviderWrapper } from "@/dashboard/shared/context/SocketContext";
import { RightSidebarProvider } from "@/dashboard/shared/context/RightSidebarContext";
import { DetailSideSheetProvider } from "@/dashboard/shared/context/DetailSideSheetContext";
import { createRoot } from "react-dom/client";
import { injectStore } from "@/utils/api";
import { logoutClient } from "@/slices/auth";
import { baseApi } from "@/api/baseApi";

if (appStage === "production") {
  log.setLevel("info");
} else {
  log.setLevel("debug");
}

log.debug("Commit Hash:", __COMMIT_HASH__);

const enhancers: StoreEnhancer[] = [];
if (nodeEnv === "production") {
  enhancers.push(
    Sentry.createReduxEnhancer({
      stateTransformer: (state) => {
        const transformedState = { ...state };
        delete transformedState.bootstrap;
        return transformedState;
      },
    })
  );
}

// Import individual reducers directly
import authReducer from "@/slices/auth";
import jobsReducer from "@/slices/jobs";

const store = configureStore({
  reducer: {
    auth: authReducer,
    jobs: jobsReducer,
    [baseApi.reducerPath]: baseApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(baseApi.middleware),
  enhancers: (getDefaultEnhancers) =>
    getDefaultEnhancers().concat(...enhancers),
});

// Debug: Check initial state
log.debug("Initial store state:", store.getState());
log.debug("Store state keys:", Object.keys(store.getState()));

injectStore(store);

if (nodeEnv === "production") {
  Sentry.init({
    dsn: "https://aae548f3ce8e436cae012d07ed0c2626@o375835.ingest.sentry.io/5195866",
    release: __COMMIT_HASH__,
    environment: appStage || nodeEnv,
    integrations: [new Integrations.BrowserTracing(), new Sentry.Replay()],
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    tracesSampleRate: 0.2,
    normalizeDepth: 6,
    maxBreadcrumbs: 25,
  });
}

function ErrorFallback() {
  return <div>An error has occurred</div>;
}
const errorFallback = <ErrorFallback />;

export function startApp() {
  const Root = () => (
    <Provider store={store}>
      <GoogleOAuthProvider clientId={googleClientId}>
        <Sentry.ErrorBoundary fallback={errorFallback} showDialog>
          <SocketProviderWrapper>
            <RightSidebarProvider>
              <DetailSideSheetProvider>
                <App />
              </DetailSideSheetProvider>
            </RightSidebarProvider>
          </SocketProviderWrapper>
        </Sentry.ErrorBoundary>
      </GoogleOAuthProvider>
    </Provider>
  );

  const root = createRoot(document.getElementById("root")!);
  root.render(<Root />);

  const bc = new BroadcastChannel("logout_channel");
  bc.onmessage = (event) => {
    if (event.data === "logout") {
      store.dispatch(logoutClient());
    }
  };
}
